/**
 * Summary Cards — 3 cards (Amount, Risk Score, AI Recommendation) +
 *                  2 flag cards (Price, Vendor). Pure presentational.
 */
import { flagClass, actionClass, priceLabel, vendorLabel, fmtAmt } from "@/lib/flags";

export default function SummaryCards({ pr }) {
  const isForeign = pr.currency && pr.currency !== "INR";
  return (
    <>
      {/* Summary row — Amount / Risk / AI Recommendation */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="border border-slate-200 rounded-lg p-3 col-span-2 sm:col-span-1">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center justify-between gap-2">
            <span>Amount</span>
            {isForeign && (
              <span
                data-testid="pr-currency-badge"
                className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 leading-none"
                title={`PR entered in ${pr.currency} · 1 ${pr.currency} = ₹${fmtAmt(pr.exch_rate)}`}
              >
                {pr.currency} @ {fmtAmt(pr.exch_rate)}
              </span>
            )}
          </div>
          <div className="text-base sm:text-lg font-bold num text-slate-900 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
            ₹ {fmtAmt(pr.amount)}
          </div>
          {isForeign && (
            <div className="text-[10px] text-slate-500 mt-0.5 truncate">
              ({pr.currency} {fmtAmt(pr.amount / (pr.exch_rate || 1))} × {fmtAmt(pr.exch_rate)})
            </div>
          )}
        </div>

        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-slate-500">Risk Score</div>
          <div className="text-lg font-bold num text-slate-900 mt-1">{pr.risk_score}</div>
        </div>

        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-slate-500">AI Recommendation</div>
          <div className="mt-1">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${actionClass(pr.ai_action)}`}>
              {pr.ai_action}
            </span>
          </div>
          {pr.hold_info?.source === "manual" && (
            <div className="text-[11px] text-slate-600 mt-1.5 leading-snug" data-testid="pr-manual-hold-info">
              Held by <span className="font-semibold text-slate-800">{pr.hold_info.by_name || "—"}</span>
              {pr.hold_info.at ? ` · ${pr.hold_info.at}` : ""}
              {pr.hold_info.note ? <div className="mt-0.5 italic">“{pr.hold_info.note}”</div> : null}
            </div>
          )}
          {pr.hold_info?.source === "ai" && (
            <div className="text-[11px] text-slate-500 mt-1.5">AI risk score &gt; 60 → suggested HOLD</div>
          )}
        </div>
      </div>

      {/* Flags row — Price / Vendor */}
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Price Flag</div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${flagClass(pr.price_flag.flag)}`}>
              {priceLabel(pr.price_flag)}
            </span>
            <span className="text-xs text-slate-600">{pr.price_flag.label}</span>
          </div>
        </div>
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Vendor Flag</div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${flagClass(pr.vendor_flag.flag)}`}>
              {vendorLabel(pr.vendor_flag)}
            </span>
            <span className="text-xs text-slate-600">{pr.vendor_flag.label}</span>
          </div>
        </div>
      </div>
    </>
  );
}
